import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import reviewDiscussionService from './reviewDiscussionService';

import type {
    AiAgendaResponse,
    HandoutPreviewData,
    ReviewDiscussionData,
    ReviewDiscussionUpdatePayload,
    TranscriptUploadResponse,
} from '@/types/review/ReviewDiscussion';

import { ReviewQueryKey } from '@/constants/QueryKeys';

export const REVIEW_DISCUSSION_QUERY_KEYS = {
    discussion: (reviewInstanceId: string) => ['review-discussion', reviewInstanceId],
    handoutPreview: (reviewInstanceId: string) => [
        'review-discussion-handout-preview',
        reviewInstanceId,
    ],
};

export const useGetReviewDiscussion = (reviewInstanceId: string) => {
    return useQuery<ReviewDiscussionData>({
        queryKey: REVIEW_DISCUSSION_QUERY_KEYS.discussion(reviewInstanceId),
        queryFn: () => reviewDiscussionService.getOrCreateDiscussion(reviewInstanceId),
        enabled: !!reviewInstanceId,
    });
};

export const useUpdateReviewDiscussion = (reviewInstanceId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ReviewDiscussionData, Error, ReviewDiscussionUpdatePayload>({
        mutationFn: (payload) =>
            reviewDiscussionService.updateDiscussion(reviewInstanceId, payload),
        onSuccess: (data) => {
            // The PUT returns the full updated discussion, so seed the cache directly instead of refetching.
            queryClient.setQueryData(
                REVIEW_DISCUSSION_QUERY_KEYS.discussion(reviewInstanceId),
                data,
            );
            // The handout preview is server-derived from many sources (not carried in this response), so it
            // still needs a refetch.
            queryClient.invalidateQueries({
                queryKey: REVIEW_DISCUSSION_QUERY_KEYS.handoutPreview(reviewInstanceId),
            });
        },
    });
};

export const useGetHandoutPreview = (reviewInstanceId: string) => {
    return useQuery<HandoutPreviewData>({
        queryKey: REVIEW_DISCUSSION_QUERY_KEYS.handoutPreview(reviewInstanceId),
        queryFn: () => reviewDiscussionService.getHandoutPreview(reviewInstanceId),
        enabled: !!reviewInstanceId,
    });
};

export const useGenerateAgendaAi = (reviewInstanceId: string) => {
    const queryClient = useQueryClient();

    return useMutation<AiAgendaResponse, Error>({
        mutationFn: () => reviewDiscussionService.generateAgendaAi(reviewInstanceId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: REVIEW_DISCUSSION_QUERY_KEYS.discussion(reviewInstanceId),
            });
        },
    });
};

export const useUploadTranscript = (reviewInstanceId: string) => {
    const queryClient = useQueryClient();

    return useMutation<TranscriptUploadResponse, Error, string>({
        mutationFn: (transcriptText) =>
            reviewDiscussionService.uploadTranscript(reviewInstanceId, transcriptText),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: REVIEW_DISCUSSION_QUERY_KEYS.discussion(reviewInstanceId),
            });
        },
    });
};

export const useCompleteEvaluation = (reviewInstanceId: string) => {
    const queryClient = useQueryClient();

    return useMutation<{ success: boolean }, Error>({
        mutationFn: () => reviewDiscussionService.completeEvaluation(reviewInstanceId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: REVIEW_DISCUSSION_QUERY_KEYS.discussion(reviewInstanceId),
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.CYCLE_INSTANCE],
            });
        },
    });
};
